/**
 * Transport shared by every Kapture frontend.
 *
 * Owns the three cross-cutting concerns call sites would otherwise repeat:
 *
 *   - `Authorization`, from whatever token store the app supplies.
 *   - The push headers from spec §4 — `Idempotency-Key` (the sync task id,
 *     which is what makes at-least-once delivery effectively-once) and
 *     `If-Match` (the base version, without which the server does no
 *     concurrency check at all).
 *   - Refresh-on-401, at most once per request and shared across concurrent
 *     callers. A drain firing 400 requests must produce one refresh, not 400:
 *     each rotation revokes the previous refresh token, so a stampede would
 *     invalidate itself.
 *
 * Token *storage* is the app's business — the phone persists a refresh token
 * in IndexedDB to fund a week offline, while a desktop workspace has no such
 * need — so it is injected rather than assumed.
 */
import { ApiError, NetworkError, TimeoutError, parseErrorText } from './errors';

export interface RequestOptions {
  /** Sent as `Idempotency-Key`; use the sync task id. */
  idempotencyKey?: string;
  /** Sent as `If-Match`. Null or undefined means no concurrency check. */
  ifMatch?: number | null;
  signal?: AbortSignal;
  /**
   * Give up after this long and raise `TimeoutError`.
   *
   * Covers the whole exchange — connect, upload, and reading the response
   * body — because a stall can happen at any of the three and the queue
   * cannot tell them apart. Omitted means no deadline, which is the right
   * default for an interactive call the user can navigate away from and the
   * wrong one for a background drain: nothing else ever cancels a `fetch`, so
   * an upload into a captive portal that accepts bytes and never answers
   * holds its task in `uploading` for as long as the app is open.
   */
  timeoutMs?: number;
  headers?: Record<string, string>;
}

export interface TokenStore {
  /** Current access token, or null when signed out. Synchronous by design:
   *  it is read on every request. */
  getAccessToken: () => string | null;
  /** Exchange the stored refresh token for a new pair. Returns false when
   *  there is nothing to refresh with, or the server rejected it. */
  refresh: () => Promise<boolean>;
}

export interface ApiClient {
  get: <T>(path: string, opts?: RequestOptions) => Promise<T>;
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
  delete: <T>(path: string, opts?: RequestOptions) => Promise<T>;
}

interface FetchInit extends RequestOptions {
  method: string;
  body?: unknown;
}

export function createApiClient(config: {
  baseUrl: string;
  tokens: TokenStore;
}): ApiClient {
  const { baseUrl, tokens } = config;
  let refreshInFlight: Promise<boolean> | null = null;

  function refreshOnce(): Promise<boolean> {
    refreshInFlight ??= tokens.refresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  function buildHeaders(init: FetchInit): Headers {
    const headers = new Headers({ accept: 'application/json', ...init.headers });

    // FormData sets its own multipart boundary; setting content-type by hand
    // produces a boundary-less header the server can't parse.
    if (init.body !== undefined && !(init.body instanceof FormData)) {
      headers.set('content-type', 'application/json');
    }

    const token = tokens.getAccessToken();
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init.idempotencyKey) headers.set('idempotency-key', init.idempotencyKey);
    if (init.ifMatch != null) headers.set('if-match', String(init.ifMatch));

    return headers;
  }

  /**
   * Return type is `FormData | string | undefined` rather than `BodyInit`.
   *
   * `BodyInit` is a DOM lib type, and this package is consumed by React
   * Native, whose type surface names it `BodyInit_`. Spelling out the two
   * shapes this function actually returns keeps the module compiling on both
   * without pulling the DOM lib into a runtime that has no DOM — which would
   * also let `document` and `window` typecheck there, and they do not exist.
   */
  function encodeBody(body: unknown): FormData | string | undefined {
    if (body === undefined || body === null) return undefined;
    if (body instanceof FormData) return body;
    return JSON.stringify(body);
  }

  /** One completed exchange: status plus the body, already read as text. */
  interface Attempt {
    ok: boolean;
    status: number;
    statusText: string;
    text: string;
  }

  /**
   * One request, start to finish, under its own deadline.
   *
   * A fresh `AbortController` per call, and that is not incidental: `request`
   * re-sends on 401, and an aborted signal stays aborted for ever. Reusing one
   * controller across both attempts would mean a first attempt that timed out
   * could never be retried — the second `fetch` would reject synchronously
   * against the already-tripped signal, turning one slow request into a
   * failure that no refresh could rescue.
   *
   * The caller's own signal is linked in rather than passed through, so a
   * screen unmounting still cancels the work while the deadline stays ours.
   *
   * The body is read here, inside the timed window, because that is where a
   * large response can stall — and it is read exactly once, since a `Response`
   * body is a stream. The timer is cleared only after that read completes; a
   * `finally` around the `fetch` alone would disarm the deadline precisely
   * when the slow part was about to begin.
   */
  async function attempt(path: string, init: FetchInit): Promise<Attempt> {
    const controller = new AbortController();
    let timedOut = false;
    const timer =
      init.timeoutMs != null
        ? setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, init.timeoutMs)
        : null;

    const onCallerAbort = () => controller.abort();
    init.signal?.addEventListener('abort', onCallerAbort);

    try {
      // Checked before the `fetch` rather than left to it. A caller that has
      // already given up — an unmounted screen replaying its last request —
      // should not have a body encoded and a connection opened on its behalf
      // just to be torn down a tick later.
      if (init.signal?.aborted) throw new Error('request was aborted before it was sent');

      const res = await fetch(`${baseUrl}${path}`, {
        method: init.method,
        headers: buildHeaders(init),
        body: encodeBody(init.body),
        signal: controller.signal,
      });

      // 204 has no body to read, and asking for one on some runtimes hangs
      // until the connection closes rather than returning empty.
      //
      // A body that cannot be read is not allowed to erase the status. The
      // status is the half that decides whether the queue retries at all, and
      // losing a 403 to an unreadable error page would turn a task that needs
      // a person into one that retries until it hits the attempt ceiling.
      let text = '';
      if (res.status !== 204) {
        try {
          text = await res.text();
        } catch {
          // Truncated or already-consumed body. Carry on with the status.
        }
      }
      return { ok: res.ok, status: res.status, statusText: res.statusText, text };
    } catch (err) {
      // Distinguishing "no response" from "rejected by the server" matters:
      // the queue retries the first indefinitely and gives up on most of the
      // second. Our own deadline is a more specific case of the same thing.
      throw timedOut ? new TimeoutError(init.timeoutMs ?? 0) : new NetworkError(err);
    } finally {
      if (timer !== null) clearTimeout(timer);
      init.signal?.removeEventListener('abort', onCallerAbort);
    }
  }

  async function request<T>(path: string, init: FetchInit): Promise<T> {
    let res = await attempt(path, init);

    if (res.status === 401 && (await refreshOnce())) {
      res = await attempt(path, init);
    }

    if (!res.ok) {
      throw new ApiError(res.status, parseErrorText(res.text), res.statusText);
    }

    if (res.status === 204) return undefined as T;

    return (res.text ? JSON.parse(res.text) : undefined) as T;
  }

  return {
    get: (path, opts = {}) => request(path, { ...opts, method: 'GET' }),
    post: (path, body, opts = {}) => request(path, { ...opts, method: 'POST', body }),
    put: (path, body, opts = {}) => request(path, { ...opts, method: 'PUT', body }),
    patch: (path, body, opts = {}) => request(path, { ...opts, method: 'PATCH', body }),
    delete: (path, opts = {}) => request(path, { ...opts, method: 'DELETE' }),
  };
}
